import type { NormalizedApiError, NormalizedApiErrorType } from "./NormalizedApiError";
import { HTTP_ERROR_MESSAGES } from "./httpErrorMessages";
import { isTechnicalError } from "./technicalErrorPatterns";

/**
 * Формат v1 (старый): ApiHelper.onError()
 * { status, result, errors: string|string[]|object, customErrors, data }
 */
interface BackendV1Response {
    status?: number;
    result?: string;
    errors?: string | string[] | Record<string, unknown>;
    customErrors?: unknown[];
    validationErrors?: Record<string, string[]>;
    data?: unknown;
}

/**
 * Формат v2 (новый): Handler.formatForV2()
 * { message, timestamp, errors?: Record<string, string[]>, exception?, file?, line?, trace? }
 */
interface BackendV2Response {
    message?: string;
    timestamp?: string;
    errors?: Record<string, string[]> | string[];
    exception?: string;
    file?: string;
    line?: number;
    trace?: unknown[];
}

type BackendResponse = BackendV1Response & BackendV2Response;

function isObject(val: unknown): val is Record<string, unknown> {
    return typeof val === "object" && val !== null && !Array.isArray(val);
}

function isSafeBackendMessage(message: string, status?: number): boolean {
    if (isTechnicalError(message)) {
        return false;
    }

    if (status !== undefined && status >= 500) {
        return false;
    }

    if (status === 404 && /^not found$/i.test(message.trim())) {
        return false;
    }

    return true;
}

/**
 * Извлекает первое человеко-читаемое сообщение из данных ответа бэка.
 * Приоритет: v2.message → v1.errors (строка) → v1.errors (массив строк) → null
 */
function extractHumanMessage(data: BackendResponse, status?: number): string | null {
    // v2: поле message (строка, русский текст задан в formatForV2)
    if (typeof data.message === "string" && data.message.trim()) {
        const msg = data.message.trim();
        if (isSafeBackendMessage(msg, status)) {
            return msg;
        }
    }

    // v1: поле errors — строка
    if (typeof data.errors === "string" && data.errors.trim()) {
        const msg = data.errors.trim();
        if (isSafeBackendMessage(msg, status)) {
            return msg;
        }
    }

    // v1: поле errors — массив строк (берём первое непустое непустое)
    if (Array.isArray(data.errors)) {
        for (const item of data.errors) {
            if (typeof item === "string" && item.trim()) {
                const msg = item.trim();
                if (isSafeBackendMessage(msg, status)) {
                    return msg;
                }
            }
        }
    }

    return null;
}

/**
 * Извлекает ошибки валидации по полям.
 * Поддерживает форматы v1.validationErrors и v2.errors (объект ключ → массив строк).
 */
function extractFieldErrors(data: BackendResponse): Record<string, string[]> | undefined {
    // v1: validationErrors
    if (isObject(data.validationErrors)) {
        const result: Record<string, string[]> = {};
        for (const [key, val] of Object.entries(data.validationErrors)) {
            if (Array.isArray(val)) {
                result[key] = val.map(String);
            } else if (typeof val === "string") {
                result[key] = [val];
            }
        }
        if (Object.keys(result).length > 0) return result;
    }

    // v2: errors как объект (ValidationException)
    if (isObject(data.errors)) {
        const result: Record<string, string[]> = {};
        for (const [key, val] of Object.entries(data.errors)) {
            if (Array.isArray(val)) {
                result[key] = val.map(String);
            } else if (typeof val === "string") {
                result[key] = [val];
            }
        }
        if (Object.keys(result).length > 0) return result;
    }

    return undefined;
}

/**
 * Собирает строку из ошибок валидации для отображения пользователю.
 */
function formatFieldErrors(fieldErrors: Record<string, string[]>): string {
    return Object.values(fieldErrors)
        .flat()
        .filter(Boolean)
        .join(". ");
}

/**
 * Определяет тип ошибки по HTTP-статусу.
 */
function getErrorTypeByStatus(status: number): NormalizedApiErrorType {
    if (status === 401) return "authorization";
    if (status === 403) return "forbidden";
    if (status === 404) return "not_found";
    if (status === 409) return "conflict";
    if (status === 422) return "validation";
    if (status >= 500) return "server";
    if (status >= 400) return "business";
    return "unknown";
}

function getErrorCodeByStatus(status?: number): string {
    if (status === 401) return "401";
    if (status === 403) return "403";
    if (status === 404) return "404";
    if (status === 409) return "409";
    if (status === 422) return "422";
    if (status !== undefined && status >= 500) return "500";
    if (status !== undefined && status >= 400) return "400";
    return "001";
}

export interface NormalizeApiErrorOptions {
    method?: string;
    url?: string;
}

/**
 * Нормализует ошибку из RTK Query payload в единый формат NormalizedApiError.
 *
 * Поддерживаемые форматы бэка:
 * - v1 (ApiHelper.onError): { status, result, errors, customErrors, data }
 * - v2 (Handler.formatForV2): { message, timestamp, errors?, exception?, ... }
 * - FETCH_ERROR / TIMEOUT_ERROR / PARSING_ERROR — нет соединения / таймаут / невалидный JSON
 */
export function normalizeApiError(
    payload: unknown,
    options: NormalizeApiErrorOptions = {},
): NormalizedApiError {
    const { method, url } = options;

    // Нет соединения с сервером
    if (isObject(payload) && payload.status === "FETCH_ERROR") {
        return {
            type: "network",
            code: "100",
            message: "Не удалось подключиться к серверу.\nПроверьте интернет или попробуйте позже.",
            method,
            url,
            originalError: payload,
        };
    }

    // Таймаут
    if (isObject(payload) && payload.status === "TIMEOUT_ERROR") {
        return {
            type: "timeout",
            code: "101",
            message: "Сервер долго не отвечает.\nПопробуйте повторить действие.",
            method,
            url,
            originalError: payload,
        };
    }

    // Ошибка парсинга JSON
    if (isObject(payload) && payload.status === "PARSING_ERROR") {
        return {
            type: "parsing",
            code: "102",
            message: "Сервер вернул некорректный ответ.\nПопробуйте позже.",
            method,
            url,
            originalError: payload,
        };
    }

    // Числовой HTTP-статус
    const status = isObject(payload) && typeof payload.status === "number"
        ? payload.status
        : undefined;

    const data: BackendResponse = isObject(payload) && isObject(payload.data)
        ? (payload.data as BackendResponse)
        : {};

    const type: NormalizedApiErrorType = status !== undefined
        ? getErrorTypeByStatus(status)
        : "unknown";

    // Ошибки валидации по полям
    const fieldErrors = extractFieldErrors(data);

    // Если есть ошибки по полям — формируем из них сообщение
    if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        const fieldMessage = formatFieldErrors(fieldErrors);
        return {
            type: "validation",
            status,
            code: getErrorCodeByStatus(status),
            message: (fieldMessage || HTTP_ERROR_MESSAGES[422]) ?? "Проверьте правильность заполнения данных.",
            fieldErrors,
            method,
            url,
            originalError: payload,
        };
    }

    // Человеко-читаемое сообщение из тела ответа
    const humanMessage = extractHumanMessage(data, status);

    // Технические детали (для логов/отладки)
    const technicalDetails: string[] = [];
    if (typeof data.message === "string" && isTechnicalError(data.message)) {
        technicalDetails.push(data.message);
    }
    if (typeof data.errors === "string" && isTechnicalError(data.errors)) {
        technicalDetails.push(data.errors);
    }
    if (Array.isArray(data.errors)) {
        data.errors.forEach((item) => {
            if (typeof item === "string" && isTechnicalError(item)) {
                technicalDetails.push(item);
            }
        });
    }

    // Финальное сообщение: из бэка → HTTP fallback → generic
    const message =
        humanMessage ??
        (status !== undefined ? HTTP_ERROR_MESSAGES[status] : undefined) ??
        "Не удалось выполнить действие. Попробуйте позже.";

    return {
        type,
        status,
        code: getErrorCodeByStatus(status),
        message,
        details: technicalDetails.length > 0 ? technicalDetails : undefined,
        method,
        url,
        originalError: payload,
    };
}
